import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/modal.jsx';
import { useToast } from '../ui/toast.jsx';
import { api, ApiError } from '../../api/index.js';

const ROLES = ['reader', 'writer', 'admin'];

function RoleBadge({ role }) {
  const tones = {
    admin:  'border-accent/30 bg-accent-soft text-accent',
    writer: 'border-success/30 bg-success-soft text-success',
    reader: 'border-line bg-canvas text-mist',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tones[role] || tones.reader}`}>
      {role}
    </span>
  );
}

/**
 * Members management for a channel.
 * - For OSS channels: read-only list (members come from the OSS tree).
 * - For custom channels: add / role-change / remove via the API.
 */
export function MembersModal({ open, channel, onClose, onChanged }) {
  const toast = useToast();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Picker state for adding new members (custom only).
  const [allPersons, setAllPersons] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerRole, setPickerRole] = useState('writer');
  const [adding, setAdding] = useState(false);

  const isOss = channel?.source === 'oss';

  const loadMembers = async () => {
    if (!channel) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.channels.members(channel.id);
      setMembers(r.members || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !channel) return;
    loadMembers();
    if (channel.source === 'custom') {
      api.persons.list().then((r) => setAllPersons(r.persons || [])).catch(() => setAllPersons([]));
    }
  }, [open, channel]);

  const memberIds = useMemo(() => new Set(members.map((m) => m.id)), [members]);
  const candidates = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    return allPersons.filter((p) => {
      if (memberIds.has(p.id)) return false;
      if (!q) return true;
      const blob = `${p.displayName} ${p.callsign || ''} ${p.position || ''} ${p.rank || ''}`.toLowerCase();
      return blob.includes(q);
    });
  }, [allPersons, memberIds, pickerQuery]);

  async function addMember(person) {
    setAdding(true);
    try {
      await api.channels.addMember(channel.id, person.id, pickerRole);
      toast.success(`Added ${person.displayName} as ${pickerRole}.`);
      setPickerQuery('');
      await loadMembers();
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Failed to add member.');
    } finally {
      setAdding(false);
    }
  }

  async function changeRole(personId, role) {
    try {
      await api.channels.setMemberRole(channel.id, personId, role);
      await loadMembers();
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Role change failed.');
    }
  }

  async function removeMember(personId) {
    try {
      await api.channels.removeMember(channel.id, personId);
      toast.success('Member removed.');
      await loadMembers();
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Remove failed.');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Members — ${channel?.name || ''}`}
      description={
        isOss
          ? 'OSS-derived. Membership reflects the unit hierarchy and is read-only here. Move people in OSS to change the roster.'
          : 'Add or remove personnel and assign roles. Roles: reader (no write), writer (default), admin (manage members).'
      }
      size="lg"
      footer={
        <button type="button" onClick={onClose} className="btn-primary" data-skip-autofocus>
          Close
        </button>
      }
    >
      {error && (
        <div role="alert" className="mb-3 rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
          <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
          {error}
        </div>
      )}

      {!isOss && (
        <div className="mb-4 rounded-2xl border border-line bg-canvas px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-mist">
              Add member
            </div>
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              className="btn-ghost text-xs"
            >
              <i className={`fa-solid fa-chevron-${pickerOpen ? 'up' : 'down'}`} aria-hidden="true" />
              {pickerOpen ? 'Hide picker' : 'Open picker'}
            </button>
          </div>
          {pickerOpen && (
            <div className="mt-2 space-y-2">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <i className="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-mist" aria-hidden="true" />
                  <input
                    type="search"
                    className="field pl-9 text-[13px]"
                    placeholder="Search by name, callsign, position, rank…"
                    value={pickerQuery}
                    onChange={(e) => setPickerQuery(e.target.value)}
                  />
                </div>
                <select
                  className="field max-w-[120px] text-[13px]"
                  value={pickerRole}
                  onChange={(e) => setPickerRole(e.target.value)}
                >
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <ul className="max-h-48 overflow-y-auto rounded-xl border border-line bg-white scrollbar-thin">
                {candidates.length === 0 ? (
                  <li className="px-3 py-4 text-center text-xs text-mist">
                    {pickerQuery ? 'No matching personnel.' : 'No personnel left to add.'}
                  </li>
                ) : (
                  candidates.slice(0, 50).map((p) => (
                    <li key={p.id} className="flex items-center gap-2 border-b border-line/60 px-3 py-2 last:border-b-0">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-canvas text-[11px] font-semibold text-mist">
                        {(p.displayName || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 truncate">
                          {p.rank && <span className="font-mono text-[10px] uppercase text-mist">{p.rank}</span>}
                          <span className="truncate text-sm text-ink">{p.displayName}</span>
                          {p.callsign && (
                            <span className="rounded border border-line bg-canvas px-1 py-0.5 font-mono text-[10px] text-mist">{p.callsign}</span>
                          )}
                        </div>
                        {p.position && <div className="truncate text-[11px] text-mist">{p.position}</div>}
                      </div>
                      <button
                        type="button"
                        onClick={() => addMember(p)}
                        disabled={adding}
                        className="btn-ghost text-xs"
                      >
                        <i className="fa-solid fa-plus" aria-hidden="true" /> Add
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl border border-line bg-white">
        <div className="flex items-center justify-between border-b border-line bg-canvas/40 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-mist">
          <span>Current members ({members.length})</span>
          {loading && <span className="text-mist"><i className="fa-solid fa-spinner fa-spin" aria-hidden="true" /></span>}
        </div>
        {members.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-mist">
            {isOss ? 'No personnel under this unit yet.' : 'No members yet.'}
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {members.map((m) => (
              <li key={m.id} className="flex items-center gap-3 px-4 py-2.5">
                <div className={'flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-semibold ' + (m.hasDevice ? 'bg-accent-soft text-accent' : 'bg-canvas text-mist')}>
                  {(m.displayName || '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {m.rank && <span className="font-mono text-[10px] uppercase text-mist">{m.rank}</span>}
                    <span className="truncate text-sm font-semibold text-ink">{m.displayName}</span>
                    {m.callsign && (
                      <span className="rounded border border-line bg-canvas px-1.5 py-0.5 font-mono text-[10px] text-mist">{m.callsign}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-mist">
                    {m.position || '—'} · {m.unitName}
                  </div>
                </div>
                {isOss ? (
                  <RoleBadge role={m.role} />
                ) : (
                  <>
                    <select
                      className="field max-w-[110px] text-[12px]"
                      value={m.role}
                      onChange={(e) => changeRole(m.id, e.target.value)}
                    >
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeMember(m.id)}
                      title="Remove"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-mist transition-colors hover:bg-rose-soft hover:text-rose-600"
                    >
                      <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
